package top.wcpe.allincore.dsl.ir.codec

/**
 * [IrByteWriter] 的纯 Kotlin 实现：以可增长 [ByteArray] 为后备，全平台可用（JVM / JS）。
 *
 * 字节序与 Netty `ByteBuf` 完全一致——**大端（big-endian）**，保证经本实现与经
 * `ByteBufIrWriter` 写出的字节逐字节相同（golden 守护）。
 *
 * 非线程安全：单次编码在单线程内完成。
 */
class ByteArrayIrWriter(initialCapacity: Int = 256) : IrByteWriter {

    private var buffer: ByteArray = ByteArray(if (initialCapacity < 16) 16 else initialCapacity)
    private var size: Int = 0

    /** 确保还能再写 [extra] 个字节，容量不足时按倍增扩容。 */
    private fun ensure(extra: Int) {
        val need = size + extra
        if (need <= buffer.size) return
        var newCap = buffer.size
        while (newCap < need) {
            newCap = newCap shl 1
        }
        buffer = buffer.copyOf(newCap)
    }

    override fun writeByte(value: Int) {
        ensure(1)
        buffer[size++] = value.toByte()
    }

    override fun writeShort(value: Int) {
        ensure(2)
        // 大端：高字节先写。
        buffer[size++] = (value ushr 8).toByte()
        buffer[size++] = value.toByte()
    }

    override fun writeInt(value: Int) {
        ensure(4)
        buffer[size++] = (value ushr 24).toByte()
        buffer[size++] = (value ushr 16).toByte()
        buffer[size++] = (value ushr 8).toByte()
        buffer[size++] = value.toByte()
    }

    override fun writeDouble(value: Double) {
        // 与 ByteBuf.writeDouble 一致：IEEE 754 位模式按大端 8 字节写。
        val bits = value.toRawBits()
        ensure(8)
        buffer[size++] = (bits ushr 56).toByte()
        buffer[size++] = (bits ushr 48).toByte()
        buffer[size++] = (bits ushr 40).toByte()
        buffer[size++] = (bits ushr 32).toByte()
        buffer[size++] = (bits ushr 24).toByte()
        buffer[size++] = (bits ushr 16).toByte()
        buffer[size++] = (bits ushr 8).toByte()
        buffer[size++] = bits.toByte()
    }

    override fun writeBytes(bytes: ByteArray) {
        ensure(bytes.size)
        bytes.copyInto(buffer, size)
        size += bytes.size
    }

    override fun writerIndex(): Int = size

    override fun setInt(index: Int, value: Int) {
        // 在已写区间回填 4 字节大端，不移动写游标。
        buffer[index] = (value ushr 24).toByte()
        buffer[index + 1] = (value ushr 16).toByte()
        buffer[index + 2] = (value ushr 8).toByte()
        buffer[index + 3] = value.toByte()
    }

    /** 导出当前已写字节的独立副本。 */
    fun toByteArray(): ByteArray = buffer.copyOf(size)
}
